import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PencilSimple, Trash, SignIn, User as UserIcon, CaretLeft, CaretRight } from "@phosphor-icons/react";

const ACTION_META = {
  create: { Icon: Plus, color: "text-emerald-700", bg: "bg-emerald-50" },
  update: { Icon: PencilSimple, color: "text-blue-700", bg: "bg-blue-50" },
  delete: { Icon: Trash, color: "text-red-700", bg: "bg-red-50" },
  login: { Icon: SignIn, color: "text-zinc-700", bg: "bg-zinc-100" },
};

const PAGE_SIZE = 25;

export default function AuditLog() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    const params = { page, page_size: PAGE_SIZE };
    if (entity !== "all") params.entity = entity;
    if (action !== "all") params.action = action;
    if (user.role === "super_admin" && companyFilter !== "all") params.company_id = companyFilter;
    const { data } = await api.get("/audit", { params });
    setRows(data.items || []);
    setTotal(data.total || 0);
    if (user.role === "super_admin" && companies.length === 0) {
      const c = await api.get("/companies");
      setCompanies(c.data);
    }
  };

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [entity, action, companyFilter]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity, action, companyFilter, page]);

  const companyName = (id) => companies.find(c => c.id === id)?.name || "—";
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromIdx = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toIdx = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="audit-page">
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Compliance</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>
            Audit Log
          </h1>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger className="w-44" data-testid="audit-entity-filter"><SelectValue placeholder="Entity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            <SelectItem value="user">Users</SelectItem>
            <SelectItem value="company">Companies</SelectItem>
            <SelectItem value="package">Packages</SelectItem>
            <SelectItem value="invoice">Invoices</SelectItem>
          </SelectContent>
        </Select>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-40" data-testid="audit-action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="login">Login</SelectItem>
          </SelectContent>
        </Select>
        {user.role === "super_admin" && (
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="w-56" data-testid="audit-company-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All companies</SelectItem>
              {companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="audit-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">When</th>
              <th className="px-5 py-3 font-semibold">Actor</th>
              <th className="px-5 py-3 font-semibold">Action</th>
              <th className="px-5 py-3 font-semibold">Entity</th>
              {user.role === "super_admin" && <th className="px-5 py-3 font-semibold">Company</th>}
              <th className="px-5 py-3 font-semibold">Target</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-zinc-500">No audit entries.</td></tr>
            ) : rows.map((r) => {
              const meta = ACTION_META[r.action] || ACTION_META.update;
              const { Icon } = meta;
              const isOpen = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr
                    className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                    onClick={() => setExpanded(isOpen ? null : r.id)}
                    data-testid={`audit-row-${r.id}`}
                  >
                    <td className="px-5 py-3 text-xs text-zinc-500 font-mono">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-zinc-200 flex items-center justify-center">
                          <UserIcon size={11} />
                        </div>
                        <div>
                          <div className="font-medium text-zinc-900 text-xs">{r.actor_email || "—"}</div>
                          <div className="text-[10px] text-zinc-500">{r.actor_role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${meta.bg} ${meta.color}`}>
                        <Icon size={11} weight="bold" /> {r.action}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-zinc-700 capitalize">{r.entity}</td>
                    {user.role === "super_admin" && (<td className="px-5 py-3 text-zinc-700">{r.company_id ? companyName(r.company_id) : "—"}</td>)}
                    <td className="px-5 py-3"><code className="text-[11px] font-mono text-zinc-500">{r.entity_id?.slice(0, 10)}</code></td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-zinc-100 bg-zinc-50">
                      <td colSpan={6} className="px-5 py-4">
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <div className="font-semibold uppercase tracking-wider text-zinc-500 mb-1">Before</div>
                            <pre className="bg-white border border-zinc-200 rounded p-3 overflow-auto max-h-60">{r.before ? JSON.stringify(r.before, null, 2) : "—"}</pre>
                          </div>
                          <div>
                            <div className="font-semibold uppercase tracking-wider text-zinc-500 mb-1">After</div>
                            <pre className="bg-white border border-zinc-200 rounded p-3 overflow-auto max-h-60">{r.after ? JSON.stringify(r.after, null, 2) : "—"}</pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4" data-testid="audit-pagination">
        <div className="text-xs text-zinc-500" data-testid="audit-pagination-summary">
          {total === 0 ? "No entries" : (
            <>Showing <span className="font-semibold text-zinc-800">{fromIdx}–{toIdx}</span> of <span className="font-semibold text-zinc-800">{total.toLocaleString()}</span></>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(1)}
            data-testid="audit-page-first"
          >
            First
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="audit-page-prev"
          >
            <CaretLeft size={13} weight="bold" />
          </Button>
          <span className="text-xs font-semibold text-zinc-700 px-3" data-testid="audit-page-indicator">
            Page {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="audit-page-next"
          >
            <CaretRight size={13} weight="bold" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
            data-testid="audit-page-last"
          >
            Last
          </Button>
        </div>
      </div>
    </div>
  );
}
